import React from 'react';
import {
  Box, Menu, MenuItem, ListItemIcon, ListItemText, FormControl, Select, Snackbar, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';

import { PageHeader, DataTableCard, FilterChips, EntityDrawer, ConfirmDialog, FeatureUsageMeter } from '../ui';
import ProductFormDrawer from './ProductFormDrawer';
import buildProductColumns from './buildProductColumns';
import useProductsPage from './useProductsPage';

const Products = () => {
  const p = useProductsPage();

  const columns = buildProductColumns({ t: p.t, i18n: p.i18n, categoryById: p.categoryById, openRowMenu: p.openRowMenu });

  const filterOptions = [
    { value: 'all', label: p.t('All') },
    ...p.categories.map((c) => ({ value: c._id, label: c.categoryName, dotColor: c.categoryColor })),
  ];

  return (
    <Box>
      <PageHeader
        title={p.t('Products')}
        subtitle={p.t('{{count}} products across {{categories}} categories', {
          count: p.total, categories: p.categories.length, defaultValue: `${p.total} products across ${p.categories.length} categories`,
        })}
        search={{ value: p.searchInput, onChange: (e) => p.setSearchInput(e.target.value), placeholder: p.t('Search Products') }}
        actions={[
          { label: p.t('Export CSV', { defaultValue: 'Export CSV' }), icon: DownloadIcon, variant: 'outlined', onClick: p.handleExport },
          {
            label: p.t('Add product', { defaultValue: 'Add product' }), icon: AddIcon, variant: 'contained', onClick: p.openCreateDrawer,
            disabled: p.planLimit?.atLimit, disabledReason: p.planLimit?.tooltip, tourId: 'products-add',
          },
        ]}
        helpKey="products"
        meter={<FeatureUsageMeter feature="products.max" label={p.t('Products', { defaultValue: 'Products' })} />}
      />

      <DataTableCard
        status={p.effectiveStatus}
        rows={p.effectiveRows}
        columns={columns}
        toolbar={(
          <Box data-tour="products-filters">
            <FilterChips
              options={filterOptions}
              value={p.categoryFilter}
              onChange={p.setCategoryFilter}
              filtersLabel={p.t('Filters')}
            />
          </Box>
        )}
        bulkBar={{
          count: p.selection.length,
          countLabel: p.t('{{count}} selected', { count: p.selection.length, defaultValue: `${p.selection.length} selected` }),
          actions: [
            { label: p.t('Change category', { defaultValue: 'Change category' }), onClick: p.openBulkCategory },
            { label: p.t('Delete'), onClick: () => p.openDeleteConfirm(p.selectedProducts) },
          ],
          onClear: () => p.setSelection([]),
        }}
        empty={p.isNoMatch ? {
          kind: 'no-match',
          title: p.t('No products match "{{query}}"', { query: p.search, defaultValue: `No products match "${p.search}"` }),
          description: p.t('Try a different search or clear the category filter.', { defaultValue: 'Try a different search or clear the category filter.' }),
          actions: [
            { label: p.t('Clear filters', { defaultValue: 'Clear filters' }), variant: 'text', onClick: () => { p.setSearchInput(''); p.setSearch(''); p.setCategoryFilter('all'); } },
          ],
        } : {
          kind: 'none',
          icon: CategoryIcon,
          title: p.t('No products yet', { defaultValue: 'No products yet' }),
          description: p.t('Add your first product to start building the catalogue your customers order from.', { defaultValue: 'Add your first product to start building the catalogue your customers order from.' }),
          actions: [{ label: p.t('Add product', { defaultValue: 'Add product' }), variant: 'contained', onClick: p.openCreateDrawer }],
        }}
        error={{
          title: p.t("Couldn't load products", { defaultValue: "Couldn't load products" }),
          description: p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' }),
          request: p.errorInfo?.request,
          retryLabel: p.t('Try again', { defaultValue: 'Try again' }),
          onRetry: p.fetchProducts,
        }}
        permission={{
          title: p.t("You don't have access to Products", { defaultValue: "You don't have access to Products" }),
          description: p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' }),
        }}
        getRowId={(row) => row._id}
        checkboxSelection
        rowSelectionModel={p.selection}
        onRowSelectionModelChange={p.setSelection}
        onRowClick={(params, event) => {
          if (event.target.closest('.MuiCheckbox-root') || event.target.closest('button')) return;
          p.openEditDrawer(params.row);
        }}
        sortingMode="server"
        sortModel={p.sortModel}
        onSortModelChange={p.setSortModel}
        loadMore={p.remaining > 0 ? {
          label: p.t('Load {{count}} more · {{remaining}} remaining', {
            count: p.nextBatchSize, remaining: p.remaining, defaultValue: `Load ${p.nextBatchSize} more · ${p.remaining} remaining`,
          }),
          onClick: p.loadMore,
          loading: p.loadingMore,
        } : undefined}
      />

      <Menu anchorEl={p.rowMenuAnchor} open={Boolean(p.rowMenuAnchor)} onClose={p.closeRowMenu}>
        <MenuItem onClick={() => { p.openEditDrawer(p.rowMenuProduct); p.closeRowMenu(); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{p.t('Edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { p.openDeleteConfirm([p.rowMenuProduct]); p.closeRowMenu(); }}>
          <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{p.t('Delete')}</ListItemText>
        </MenuItem>
      </Menu>

      <EntityDrawer
        open={p.drawerOpen}
        onClose={p.closeDrawer}
        title={p.drawerMode === 'create' ? p.t('Create Product') : p.t('Edit Product')}
        subtitle={p.t('Added to your catalogue · stock is set separately in Inventory', { defaultValue: 'Added to your catalogue · stock is set separately in Inventory' })}
        closeLabel={p.t('Close', { defaultValue: 'Close' })}
        cancelLabel={p.t('Cancel')}
        primaryLabel={p.saving
          ? (p.drawerMode === 'create' ? p.t('Creating...') : p.t('Updating...'))
          : (p.drawerMode === 'create' ? p.t('Create') : p.t('Update'))}
        onPrimary={p.handleSave}
        primaryDisabled={p.saving}
        loading={p.saving}
      >
        <ProductFormDrawer
          t={p.t}
          form={p.form}
          setForm={p.setForm}
          formErrors={p.formErrors}
          saving={p.saving}
          categories={p.categories}
          categoryById={p.categoryById}
          displayedImages={p.displayedImages}
          handleFileInput={p.handleFileInput}
          removeStagedFile={p.removeStagedFile}
        />
      </EntityDrawer>

      <ConfirmDialog
        open={p.confirmOpen}
        onClose={() => p.setConfirmOpen(false)}
        tone="danger"
        icon={DeleteIcon}
        title={p.confirmTargets.length === 1
          ? p.t('Delete "{{name}}"?', { name: p.confirmTargets[0]?.productName, defaultValue: `Delete "${p.confirmTargets[0]?.productName}"?` })
          : p.t('Delete {{count}} products?', { count: p.confirmTargets.length, defaultValue: `Delete ${p.confirmTargets.length} products?` })}
        description={p.t('This cannot be undone. This permanently removes them from your catalog and all category filters.', { defaultValue: 'This cannot be undone. This permanently removes them from your catalog and all category filters.' })}
        records={p.confirmTargets.map((prod) => `${prod.productName}${p.inventoryByProduct[prod._id] ? ` · ${p.inventoryByProduct[prod._id]} ${p.t('units', { defaultValue: 'units' })}` : ''}`)}
        cancelLabel={p.t('Cancel')}
        confirmLabel={p.t('Delete', { defaultValue: 'Delete' })}
        onConfirm={p.handleConfirmDelete}
        loading={p.deleting}
      />

      <ConfirmDialog
        open={p.bulkOpen}
        onClose={() => p.setBulkOpen(false)}
        tone="primary"
        icon={CategoryIcon}
        title={p.t('Change category for {{count}} selected products:', { count: p.selectedProducts.length })}
        description={p.t('Choosing None removes the category from all selected products.', { defaultValue: 'Choosing None removes the category from all selected products.' })}
        records={p.selectedProducts.map((prod) => prod.productName)}
        cancelLabel={p.t('Cancel')}
        confirmLabel={p.t('Update Categories')}
        onConfirm={p.handleBulkCategorySave}
        loading={p.bulkSaving}
      >
        <FormControl fullWidth sx={{ mt: 1 }} disabled={p.bulkSaving}>
          <Select
            value={p.bulkCategoryId}
            onChange={(e) => p.setBulkCategoryId(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">{p.t('None (Remove Category)')}</MenuItem>
            {p.categories.map((category) => (
              <MenuItem key={category._id} value={category._id}>{category.categoryName}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </ConfirmDialog>

      <Snackbar
        open={p.snackbar.open}
        autoHideDuration={4000}
        onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>
          {p.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Products;
